import React, { Profiler, useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState, type ProfilerOnRenderCallback } from 'react'
import type { Tab } from '../../hooks/multiWindow'
import { ErrorBoundary } from '../../components/common/ErrorBoundary'
import { markTabEvent, measureTabPhase, measureTabRenderFromStart } from '../../lib/perf/tabProfiling'
import { ActiveTabProvider, TabIdProvider } from './ActiveTabContext'
import './TabStack.scss'

// Dev-only slow-render reporter. One frame at 60 fps = 16.67 ms; anything
// over that stutters visibly. Production builds skip the Profiler entirely
// so there's zero runtime cost.
const SLOW_RENDER_THRESHOLD_MS = 16
const isDev = process.env.NODE_ENV !== 'production'

const onSlowRender: ProfilerOnRenderCallback = (id, phase, actualDuration, _baseDuration, startTime) => {
  if (actualDuration > SLOW_RENDER_THRESHOLD_MS) {
    console.warn(
      `[TabStack/slow] ${id} (${phase}) took ${actualDuration.toFixed(1)} ms`,
    )
    // Surface the slow render on the Chrome Performance timeline so the
    // recording shows WHICH render burned the frame, not just that one did.
    // `id` is `tab:<path>` — strip the prefix to get the clean path label.
    const label = id.startsWith('tab:') ? id.slice(4) : id
    measureTabRenderFromStart(`slow-render:${phase}`, label, startTime, actualDuration)
  }
}

// A 4 px dead-zone on each edge — `scrollTop === 0` isn't always exactly 0
// on touchpads (sub-pixel scroll), so require a small threshold before the
// fade appears. Same idea for the bottom.
const FADE_THRESHOLD_PX = 4

interface TabStackProps {
  tabs: Tab[]
  activeTabId: string | null
  /**
   * Rendered per tab. Each slot wraps the result in an `ActiveTabProvider`
   * so pages can call `useIsActiveTab()` to gate global side effects
   * (keyboard listeners, document observers, paused live queries) without
   * forcing a full prop-driven re-render on every tab switch.
   */
  children: (tab: Tab) => React.ReactNode
}

/**
 * One tab's slot: owns its scroll container and tracks whether the top or
 * bottom fade should be visible (fade only when there's content above /
 * below to fade into — otherwise it would paint over the flush edge of
 * the page's own content, like the project title at the top).
 */
interface TabSlotProps {
  tab: Tab
  isActive: boolean
  wasActivated: boolean
  renderContent: (tab: Tab) => React.ReactNode
}

const TabSlotComponent: React.FC<TabSlotProps> = ({ tab, isActive, wasActivated, renderContent }) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  // Dauer des ERSTEN Mountens, auf der Konsole statt nur in der Timeline.
  //
  // `markTabEvent`/`measureTabPhase` schreiben ausschließlich User-Timing-
  // Marken; die sieht man nur in einer Performance-Aufzeichnung. Für die Frage
  // „welcher Tab kostet wie viel" reicht eine Zeile, die man kopieren kann.
  //
  // Gemessen wird von „Inhalt beginnt zu rendern" bis zum nächsten Frame, also
  // inklusive der Layout-Effekt-Kaskade und des ersten Paints — genau der
  // Zeitraum, der beim ersten Klick ruckelt.
  const mountStartRef = useRef<number | null>(null)
  if (isDev && wasActivated && mountStartRef.current === null) {
    mountStartRef.current = performance.now()
  }
  useEffect(() => {
    if (!isDev || mountStartRef.current === null) return
    const start = mountStartRef.current
    mountStartRef.current = -1
    if (start < 0) return
    requestAnimationFrame(() => {
      console.warn(`[TabStack/first-mount] ${tab.path}: ${(performance.now() - start).toFixed(0)} ms`)
    })
  }, [wasActivated, tab.path])
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)

  // Chrome-timeline instrumentation. Fires right after React commits the
  // isActive flip (before paint), then again inside rAF (≈ first paint).
  // The two marks + the `switch-request` mark set in `setActiveTab` form
  // the `commit` and `paint` spans visible in the Performance Timings track.
  const wasActiveRef = useRef(isActive)
  useLayoutEffect(() => {
    if (!isDev) return
    if (isActive && !wasActiveRef.current) {
      markTabEvent('slot-active', tab.path)
      measureTabPhase('commit', tab.path, 'switch-request', 'slot-active')
      const rafId = requestAnimationFrame(() => {
        markTabEvent('first-paint', tab.path)
        measureTabPhase('paint', tab.path, 'slot-active', 'first-paint')
      })
      wasActiveRef.current = true
      return () => cancelAnimationFrame(rafId)
    }
    if (!isActive && wasActiveRef.current) {
      wasActiveRef.current = false
    }
    return undefined
  }, [isActive, tab.path])

  // Recompute fade visibility on scroll + on content-size change. Also called
  // on mount so the initial state reflects the actual overflow even when the
  // user hasn't scrolled yet.
  const updateFades = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const canScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - FADE_THRESHOLD_PX
    const canScrollUp = el.scrollTop > FADE_THRESHOLD_PX
    setShowTopFade(prev => (prev === canScrollUp ? prev : canScrollUp))
    setShowBottomFade(prev => (prev === canScrollDown ? prev : canScrollDown))
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateFades()
    // ResizeObserver catches content-height changes (lazy-loaded months, task
    // edits) so a page that suddenly becomes scrollable shows the bottom
    // fade without needing a scroll event.
    const ro = new ResizeObserver(updateFades)
    ro.observe(el)
    for (const child of Array.from(el.children)) ro.observe(child)
    el.addEventListener('scroll', updateFades, { passive: true })
    return () => {
      ro.disconnect()
      el.removeEventListener('scroll', updateFades)
    }
  }, [updateFades, wasActivated])

  return (
    <div
      className={`tab-stack__slot${isActive ? ' tab-stack__slot--active' : ''}`}
      aria-hidden={!isActive}
      inert={!isActive}
    >
      <div ref={scrollRef} className="tab-stack__slot-scroll">
        {/* Per-slot ErrorBoundary so a crash in one tab doesn't kill
            the others. The `key={tab.id}` resets the boundary state
            when the tab is closed + re-opened later. */}
        <ErrorBoundary key={tab.id} name={`Tab:${tab.path}`} variant="inline">
          {wasActivated && (isDev ? (
            <Profiler id={`tab:${tab.path}`} onRender={onSlowRender}>
              <TabIdProvider value={tab.id}>
                <ActiveTabProvider value={isActive}>
                  {renderContent(tab)}
                </ActiveTabProvider>
              </TabIdProvider>
            </Profiler>
          ) : (
            <TabIdProvider value={tab.id}>
              <ActiveTabProvider value={isActive}>
                {renderContent(tab)}
              </ActiveTabProvider>
            </TabIdProvider>
          ))}
        </ErrorBoundary>
      </div>
      {/* Scroll fades live above the scroll wrap so they stay pinned to the
          slot's top/bottom edges while the user scrolls. Only rendered when
          there's actually something to fade — otherwise the top fade would
          paint over the page's flush header (e.g. project title). */}
      {showTopFade && (
        <div className="tab-stack__fade tab-stack__fade--top" aria-hidden="true" />
      )}
      {showBottomFade && (
        <div className="tab-stack__fade tab-stack__fade--bottom" aria-hidden="true" />
      )}
    </div>
  )
}

const TabSlot = React.memo(TabSlotComponent)

/**
 * Renders every open tab's content **in parallel** and swaps the active
 * slot via CSS. Inactive slots stay mounted — their React state,
 * `useLiveQuery` subscriptions, scroll position, expanded-task state, and
 * selection all survive tab switches.
 *
 * Why `display: none` (in SCSS) for inactive slots: `visibility: hidden`
 * lets the GPU compositor keep the hidden layer around for a beat — which
 * showed up as ghost checkboxes from the previous tab lingering for ~1 s
 * after switching. `display: none` removes the subtree from rendering
 * instantly; Chromium preserves `scrollTop` across the display toggle so
 * per-tab scroll state is still retained.
 *
 * `inert` locks focus and assistive-tech out of the hidden subtree on
 * React 19+.
 */
const TabStackComponent: React.FC<TabStackProps> = ({ tabs, activeTabId, children }) => {
  // Lazy mount: only activated tabs (ever-active-at-least-once) render their
  // content. On session-restore with 3+ tabs open, only the active one
  // mounts; the others stay as empty slots until the user first clicks them.
  //
  // Why: profiler showed 554–595 ms of layout-effect cascades when all tabs
  // mounted simultaneously. Spreading that cost across individual tab
  // activations keeps the cold-start fast and each later activation pays
  // only its own cost — never visible because the user initiated the switch.
  //
  // The set is mutated during render (safe — it's our ref and the operation
  // is idempotent). This avoids a useEffect round-trip that would otherwise
  // show one empty-slot frame before the activated tab's content paints.
  const activatedIdsRef = useRef<Set<string>>(new Set())
  const [, bumpActivated] = useReducer((n: number) => n + 1, 0)
  if (activeTabId && !activatedIdsRef.current.has(activeTabId)) {
    activatedIdsRef.current.add(activeTabId)
  }

  // Vorwärmen im Leerlauf.
  //
  // Das faule Mounten oben hat die Kaskade nicht beseitigt, sondern vom
  // Kaltstart auf den ERSTEN Klick verschoben. Der Kommentar dort nahm an, das
  // sei „never visible because the user initiated the switch" — in der Praxis
  // sind ~550ms auch dann deutlich zu sehen. Also wandert die Last dorthin, wo
  // sie wirklich niemand bemerkt: in die Leerlaufzeit nach dem ersten Paint.
  //
  // Ein Tab pro Leerlauf-Fenster, und BEWUSST ohne `timeout`: Mit Zeitlimit
  // würde Chromium den Rückruf auch unter Last erzwingen und damit genau den
  // Kaltstart zurückholen, den das faule Mounten schnell gemacht hat.
  //
  // `tabsRef` statt `tabs` in den Abhängigkeiten: Die Liste ist bei jedem
  // Rendern eine neue Referenz, und `bumpActivated` löst ein Rendern aus — mit
  // `tabs` als Abhängigkeit liefe der Effekt endlos neu an.
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs
  useEffect(() => {
    if (typeof requestIdleCallback !== 'function') return

    let handle = 0
    const step = () => {
      const next = tabsRef.current.find(t => !activatedIdsRef.current.has(t.id))
      if (!next) return
      activatedIdsRef.current.add(next.id)
      bumpActivated()
      handle = requestIdleCallback(step)
    }
    handle = requestIdleCallback(step)
    return () => cancelIdleCallback(handle)
  }, [tabs.length])

  return (
    <div className="tab-stack">
      {tabs.map(tab => (
        <TabSlot
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeTabId}
          wasActivated={activatedIdsRef.current.has(tab.id)}
          renderContent={children}
        />
      ))}
    </div>
  )
}

export const TabStack = React.memo(TabStackComponent) as typeof TabStackComponent
